import {
  CheckCircleIcon,
  DocumentCheckIcon as ClipboardCheckIcon,
  DocumentMagnifyingGlassIcon as DocumentSearchIcon,
  PresentationChartLineIcon,
  ShieldExclamationIcon,
  StarIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { RectangleStackIcon } from "@heroicons/react/24/outline";
import type { ComponentProps, ElementType } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Location,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import SearchBar from "../companies/SearchBar";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import PageHeader from "../shared-components/PageHeader";
import TabsSelection, {
  type TabProps,
} from "../shared-components/TabsSelection";
import { useFamaFlags } from "../api/FamaFlags";
import InProgressIcon from "../shared-components/icons/InProgressIcon";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import AddItemIcon from "../shared-components/icons/AddItem";
import useNavigateNewPerson from "./useNavigateNewPerson";
import { ArchiveBoxIcon } from "@heroicons/react/24/solid";
import { useLegacy360ReportsTabVisibility } from "./useLegacy360ReportsTabVisibility";

enum TabsIdentifiersEnum {
  IN_PROCESS = "IN_PROCESS",
  INTERNAL_QA = "INTERNAL_QA",
  COMPLETED = "COMPLETED",
  AMENDMENT = "AMENDMENT",
  IN_PROGRESS = "IN_PROGRESS",
  CLIENT_QA = "CLIENT_QA",
  IN_REVIEW = "IN_REVIEW",
  REVIEWED = "REVIEWED",
  LEGACY_CORE = "LEGACY_CORE",
  LEGACY_360 = "LEGACY_360",
}

type ReportTabItem = {
  identifier: string;
  /** Tab icons are Heroicons forwardRef components; `TabProps["icon"]` is narrower in TS. */
  icon: ElementType;
  label: string;
  href: string;
};

/** Outline stack icon with a heavier stroke so it reads closer to solid tab icons. */
const Legacy360ReportsTabIcon = ((
  props: ComponentProps<typeof RectangleStackIcon>
) => <RectangleStackIcon {...props} strokeWidth={2.5} />) as ElementType;

const Reports = () => {
  const { navigateCreatePerson } = useNavigateNewPerson();
  const navigate = useNavigate();
  const [, setParams] = useSearchParams();
  const location: Location = useLocation();
  const locationState = location.state as {
    reportSubmitted: boolean;
    reportsBulkUpload: boolean;
    toast: string;
  };
  const { showLargeToast } = useApplicationToast();
  const [initialTab, setInitialTab] = useState(TabsIdentifiersEnum.IN_PROCESS);
  const {
    canAddReport,
    canManageReports,
    canInProcess,
    canInternalQaView,
    canClientQaView,
    canComplete,
    canNavAmendment,
    canClientInProgress,
    canNavPendingReview,
    canNavReviewed,
    canNavLegacyCore,
  } = usePermissionValidator();
  const { showReportsClient } = useFamaFlags();
  const visibilityQuery = useLegacy360ReportsTabVisibility();

  const show360LegacyTab =
    canManageReports &&
    visibilityQuery.isSuccess &&
    visibilityQuery.data?.visible === true;

  const legacy360Tab = useMemo(
    (): ReportTabItem => ({
      identifier: TabsIdentifiersEnum.LEGACY_360,
      icon: Legacy360ReportsTabIcon,
      label: "360 Legacy Reports",
      href: "/reports/360_legacy",
    }),
    []
  );

  /** 360 Legacy tab is loaded asynchronously so the tab strip is not blocked (see plan 360 Legacy Reports UI). */
  const baseTabs = useMemo((): ReportTabItem[] => {
    const t: ReportTabItem[] = [];
    if (canInProcess) {
      t.push({
        identifier: TabsIdentifiersEnum.IN_PROCESS,
        icon: PresentationChartLineIcon,
        label: "In Process",
        href: "/reports/in_process",
      });
    }
    if (canInternalQaView) {
      t.push({
        identifier: TabsIdentifiersEnum.INTERNAL_QA,
        icon: StarIcon,
        label: "Internal Investigator",
        href: "/reports/internal_qa",
      });
    }
    if (canComplete) {
      t.push({
        identifier: TabsIdentifiersEnum.COMPLETED,
        icon: CheckCircleIcon,
        label: "Completed",
        href: "/reports/completed",
      });
    }
    if (canNavAmendment) {
      t.push({
        identifier: TabsIdentifiersEnum.AMENDMENT,
        icon: ShieldExclamationIcon,
        label: "Amendment",
        href: "/reports/amendment",
      });
    }
    if (!showReportsClient) return t;
    if (canClientInProgress) {
      t.push({
        identifier: TabsIdentifiersEnum.IN_PROGRESS,
        icon: InProgressIcon,
        label: "In Progress",
        href: "/reports/in_progress",
      });
    }
    if (canClientQaView) {
      t.push({
        identifier: TabsIdentifiersEnum.CLIENT_QA,
        icon: UserIcon,
        label: "Client Investigator",
        href: "/reports/client_qa",
      });
    }
    if (canNavPendingReview) {
      t.push({
        identifier: TabsIdentifiersEnum.IN_REVIEW,
        icon: DocumentSearchIcon,
        label: "Pending Review",
        href: "/reports/pending_review",
      });
    }
    if (canNavReviewed) {
      t.push({
        identifier: TabsIdentifiersEnum.REVIEWED,
        icon: ClipboardCheckIcon,
        label: "Reviewed",
        href: "/reports/reviewed",
      });
    }
    if (canNavLegacyCore) {
      t.push({
        identifier: TabsIdentifiersEnum.LEGACY_CORE,
        icon: ArchiveBoxIcon,
        label: "Legacy Reports",
        href: "/reports/legacy",
      });
    }
    return t;
  }, [
    canInProcess,
    canInternalQaView,
    canComplete,
    canNavAmendment,
    showReportsClient,
    canClientInProgress,
    canClientQaView,
    canNavPendingReview,
    canNavReviewed,
    canNavLegacyCore,
  ]);

  const tabs = useMemo(
    () => (show360LegacyTab ? [...baseTabs, legacy360Tab] : baseTabs),
    [baseTabs, legacy360Tab, show360LegacyTab]
  );

  useEffect(() => {
    if (locationState?.reportSubmitted && !locationState?.reportsBulkUpload) {
      showLargeToast?.({
        title: "Report Added!",
        message:
          "Report has been submitted. It will take a few minutes for it to display on the list. ",
        icon: (
          <div className="bg-green-100 text-green-600 rounded-full p-2">
            <AddItemIcon className="w-6" />
          </div>
        ),
      });
    }
    if (locationState?.reportSubmitted && locationState?.reportsBulkUpload) {
      showLargeToast?.({
        title: "Bulk Upload Submitted!",
        message:
          "The file has been uploaded. A confirmation email will be sent shortly",
        icon: (
          <div className="bg-green-100 text-green-600 rounded-full p-2">
            <AddItemIcon className="w-6" />
          </div>
        ),
      });
    }
    if (locationState?.toast) {
      showLargeToast?.({
        title: "Redirect Notice",
        message: locationState.toast,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState]);

  useEffect(() => {
    switch (location.pathname) {
      case "/reports/in_process":
      case "/reports/in_process/v2025":
      case "/reports/in_process/v2026":
        setInitialTab(TabsIdentifiersEnum.IN_PROCESS);
        break;
      case "/reports/internal_qa":
      case "/reports/internal_qa/v2025":
      case "/reports/internal_qa/v2026":
        setInitialTab(TabsIdentifiersEnum.INTERNAL_QA);
        break;
      case "/reports/client_qa":
      case "/reports/client_qa/v2025":
      case "/reports/client_qa/v2026":
        setInitialTab(TabsIdentifiersEnum.CLIENT_QA);
        break;
      case "/reports/completed":
        setInitialTab(TabsIdentifiersEnum.COMPLETED);
        break;
      case "/reports/amendment":
        setInitialTab(TabsIdentifiersEnum.AMENDMENT);
        break;
      case "/reports/in_progress":
      case "/reports/in_progress/v2025":
      case "/reports/in_progress/v2026":
        setInitialTab(TabsIdentifiersEnum.IN_PROGRESS);
        break;
      case "/reports/pending_review":
      case "/reports/pending_review/v2025":
      case "/reports/pending_review/v2026":
        setInitialTab(TabsIdentifiersEnum.IN_REVIEW);
        break;
      case "/reports/reviewed":
      case "/reports/reviewed/v2025":
      case "/reports/reviewed/v2026":
        setInitialTab(TabsIdentifiersEnum.REVIEWED);
        break;
      case "/reports/legacy":
        setInitialTab(TabsIdentifiersEnum.LEGACY_CORE);
        break;
      case "/reports/360_legacy":
        setInitialTab(TabsIdentifiersEnum.LEGACY_360);
        break;
      default:
        setInitialTab(TabsIdentifiersEnum.IN_PROCESS);
        break;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/reports/360_legacy") return;
    if (!canManageReports) {
      navigate("/reports", { replace: true });
      return;
    }
    if (
      visibilityQuery.isFetched &&
      !visibilityQuery.isFetching &&
      (!visibilityQuery.isSuccess || !visibilityQuery.data?.visible)
    ) {
      navigate("/reports", { replace: true });
    }
  }, [
    location.pathname,
    canManageReports,
    navigate,
    visibilityQuery.isFetched,
    visibilityQuery.isFetching,
    visibilityQuery.isSuccess,
    visibilityQuery.data,
  ]);

  function handleTabSelection() {
    setParams({});
  }

  function CanAddReportButton() {
    if (!canAddReport) {
      return <></>;
    }
    return (
      <Button
        type={ButtonTypes.add}
        text="Add New Report"
        colorType={ButtonColorTypes.success}
        onClick={() => navigateCreatePerson()}
      />
    );
  }

  return (
    <>
      <PageHeader title={"Reports"}>
        <div className="flex">
          {initialTab !== TabsIdentifiersEnum.AMENDMENT && (
            <SearchBar placeholder="Search by Name or Client ID" />
          )}
          <CanAddReportButton />
        </div>
      </PageHeader>
      <TabsSelection
        tabs={tabs as TabProps[]}
        initial={initialTab}
        onChange={handleTabSelection}
      ></TabsSelection>
      <div className="pt-4 relative">
        <Outlet></Outlet>
      </div>
    </>
  );
};

export default Reports;
